"use client";

import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { BedDouble, ChevronDown, ChevronRight, MapPin } from "lucide-react";
import type { DraggableProvidedDragHandleProps } from "react-beautiful-dnd";
import { Draggable } from "react-beautiful-dnd";
import { StrictModeDroppable } from "@/@libs/strict-mode-droppable";
import {
  iHolidayQuoteItineraryBlock,
  type QuoteNarrativeLayoutVariant,
} from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { ItineraryBlock } from "../itinerary-section";
import {
  quoteThemeColorStyles,
  quoteThemeCssVars,
} from "../quote-theme/quote-theme-css-vars";
import {
  QUOTE_ACCOMMODATIONS_LAYOUT_OPTIONS,
  resolveAccommodationsLayout,
} from "../quote-narrative-layout";
import { QuoteOverlayModal } from "../quote-overlay-modal";
import { useHolidayQuoteBrand } from "../contexts/use-holiday-quote-brand";
import { cn } from "@/lib/utils";
import { buildTripModeStaySummaries } from "./build-trip-mode-stay-summaries";
import { HotelStayCard } from "./hotel-stay-card";

interface TripModeAccommodationsSectionProps {
  hotels: iHolidayQuoteItineraryBlock[];
  /** Optional wrapper around each hotel card (e.g. editable chrome). */
  renderHotel?: (
    hotel: iHolidayQuoteItineraryBlock,
    index: number,
    card: ReactNode,
    dragHandleProps?: DraggableProvidedDragHandleProps | null
  ) => ReactNode;
  /** Agent-only: persist accommodations layout preference. */
  onLayoutChange?: (layout: QuoteNarrativeLayoutVariant) => Promise<void> | void;
  droppableId?: string;
  showEmpty?: boolean;
}

function hotelName(hotel: iHolidayQuoteItineraryBlock) {
  return hotel.accommodationData?.accommodationName || "Hotel";
}

function HotelCard({
  hotel,
  index,
  renderHotel,
  withAnchor = true,
  dragHandleProps,
}: {
  hotel: iHolidayQuoteItineraryBlock;
  index: number;
  renderHotel?: TripModeAccommodationsSectionProps["renderHotel"];
  withAnchor?: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
}) {
  const card = (
    <div
      id={withAnchor ? `itinerary-${hotel.id}` : undefined}
      className={withAnchor ? "scroll-mt-28" : undefined}
      key={hotel.id}
    >
      <ItineraryBlock item={hotel} index={index} />
    </div>
  );
  return renderHotel ? (
    <>{renderHotel(hotel, index, card, dragHandleProps)}</>
  ) : (
    card
  );
}

function StayTabButton({
  hotel,
  index,
  nights,
  location,
  active,
  onClick,
  orientation = "horizontal",
}: {
  hotel: iHolidayQuoteItineraryBlock;
  index: number;
  nights?: number;
  location?: string;
  active: boolean;
  onClick: () => void;
  orientation?: "horizontal" | "vertical";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-start gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors",
        orientation === "horizontal" ? "shrink-0 min-w-[11rem] max-w-[16rem]" : "w-full"
      )}
      style={
        active
          ? quoteThemeColorStyles.primaryButton
          : {
              backgroundColor: quoteThemeCssVars.primary10,
              color: quoteThemeCssVars.primary,
            }
      }
    >
      <span
        className={cn(
          "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
          active ? "bg-white/20" : "bg-white"
        )}
      >
        <BedDouble className="h-4 w-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-[11px] font-semibold uppercase tracking-wide opacity-80">
          Stay {index + 1}
          {nights ? ` · ${nights}n` : ""}
        </span>
        <span className="line-clamp-1">{hotelName(hotel)}</span>
        {location && orientation === "vertical" ? (
          <span className="mt-0.5 flex items-center gap-1 text-[11px] font-normal opacity-75">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="line-clamp-1">{location}</span>
          </span>
        ) : null}
      </span>
    </button>
  );
}

function ExpandableStay({
  hotel,
  index,
  nights,
  location,
  open,
  onToggle,
  renderHotel,
}: {
  hotel: iHolidayQuoteItineraryBlock;
  index: number;
  nights?: number;
  location?: string;
  open: boolean;
  onToggle: () => void;
  renderHotel?: TripModeAccommodationsSectionProps["renderHotel"];
}) {
  return (
    <div
      className="overflow-hidden rounded-xl border"
      style={{ borderColor: open ? quoteThemeCssVars.primary20 : undefined }}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        style={{
          backgroundColor: open ? quoteThemeCssVars.primary10 : undefined,
        }}
      >
        <div className="flex min-w-0 items-start gap-3">
          <span
            className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white"
            style={quoteThemeColorStyles.primaryBg}
          >
            <BedDouble className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p
              className="text-xs font-semibold uppercase tracking-wide"
              style={quoteThemeColorStyles.primary}
            >
              Stay {index + 1}
              {nights ? ` · ${nights} night${nights === 1 ? "" : "s"}` : ""}
            </p>
            <p className="font-medium text-gray-900">{hotelName(hotel)}</p>
            {location ? (
              <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500">
                <MapPin className="h-3 w-3 shrink-0" />
                <span className="line-clamp-1">{location}</span>
              </p>
            ) : null}
          </div>
        </div>
        <ChevronDown
          className={cn(
            "h-5 w-5 shrink-0 text-gray-500 transition-transform",
            open && "rotate-180"
          )}
        />
      </button>
      {open ? (
        <div className="border-t px-2 py-3 sm:px-4">
          <HotelCard hotel={hotel} index={index} renderHotel={renderHotel} />
        </div>
      ) : null}
    </div>
  );
}

export function TripModeAccommodationsSection({
  hotels,
  renderHotel,
  onLayoutChange,
  droppableId,
  showEmpty = false,
}: TripModeAccommodationsSectionProps) {
  const { quote } = useHolidayQuoteBrand();
  const savedLayout = resolveAccommodationsLayout(
    quote?.data?.tripAccommodationsLayout
  );
  const stayMeta = useMemo(() => {
    const map = new Map(
      buildTripModeStaySummaries(hotels).map((stay) => [stay.blockId, stay])
    );
    return map;
  }, [hotels]);

  // Optimistic layout so the UI updates before the store patch lands.
  const [layout, setLayout] =
    useState<QuoteNarrativeLayoutVariant>(savedLayout);
  const [activeIndex, setActiveIndex] = useState(0);
  // Accordion starts fully collapsed by default.
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [viewHotelId, setViewHotelId] = useState<string | null>(null);
  const [layoutSaving, setLayoutSaving] = useState(false);

  useEffect(() => {
    setLayout(savedLayout);
  }, [savedLayout]);

  useEffect(() => {
    if (activeIndex > hotels.length - 1) {
      setActiveIndex(Math.max(0, hotels.length - 1));
    }
  }, [hotels.length, activeIndex]);

  // Keep openIds keys in sync when hotels are added/removed — do NOT force
  // at least one open (that caused accordion collapse flicker).
  useEffect(() => {
    setOpenIds((prev) => {
      const hotelIds = new Set(hotels.map((h) => h.id));
      const next: Record<string, boolean> = {};
      let changed = false;
      for (const [id, open] of Object.entries(prev)) {
        if (hotelIds.has(id)) {
          next[id] = open;
        } else {
          changed = true;
        }
      }
      for (const hotel of hotels) {
        if (!(hotel.id in next)) {
          next[hotel.id] = false;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [hotels]);

  useEffect(() => {
    const handleFocus = (event: Event) => {
      const blockId = (event as CustomEvent<{ blockId?: string }>).detail
        ?.blockId;
      const index = hotels.findIndex((hotel) => hotel.id === blockId);
      if (!blockId || index === -1) return;

      setActiveIndex(index);
      setOpenIds((prev) =>
        layout === "accordion"
          ? { [blockId]: true }
          : { ...prev, [blockId]: true }
      );
      if (layout === "hotel_cards") {
        setViewHotelId(blockId);
      }

      window.setTimeout(() => {
        document
          .getElementById("trip-section-stays")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
        document
          .getElementById(`itinerary-${blockId}`)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 0);
    };

    window.addEventListener("trip-focus-itinerary", handleFocus);
    return () =>
      window.removeEventListener("trip-focus-itinerary", handleFocus);
  }, [hotels, layout]);

  if (hotels.length === 0) {
    if (!showEmpty) return null;
    return (
      <section
        id="trip-section-stays"
        className="scroll-mt-28 bg-white p-4 sm:p-6"
        style={{
          borderRadius: quoteThemeCssVars.radiusCard,
          boxShadow: quoteThemeCssVars.shadowCard,
        }}
      >
        <h2
          className="flex items-center gap-2 text-xl sm:text-2xl font-semibold"
          style={{
            color: quoteThemeCssVars.blockHeading,
            fontFamily: quoteThemeCssVars.headingFont,
          }}
        >
          <BedDouble
            className="h-6 w-6"
            style={{ color: quoteThemeCssVars.primary }}
          />
          Accommodations
        </h2>
        <p className="mt-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-500">
          Add a hotel to complete the stays section of your brochure.
        </p>
      </section>
    );
  }

  const safeIndex = Math.min(activeIndex, hotels.length - 1);
  const activeHotel = hotels[safeIndex];

  const handleLayoutChange = async (
    next: QuoteNarrativeLayoutVariant
  ) => {
    if (!onLayoutChange || next === layout) return;
    const previous = layout;
    setLayout(next);
    if (next === "accordion") {
      setOpenIds({});
    }
    setLayoutSaving(true);
    try {
      await onLayoutChange(next);
    } catch {
      setLayout(previous);
    } finally {
      setLayoutSaving(false);
    }
  };

  const metaFor = (hotel: iHolidayQuoteItineraryBlock) =>
    stayMeta.get(hotel.id);

  const viewHotel = hotels.find((hotel) => hotel.id === viewHotelId);
  const viewHotelIndex = viewHotel
    ? hotels.findIndex((hotel) => hotel.id === viewHotel.id)
    : -1;

  const renderLayouts = () => {
    if (layout === "hotel_cards") {
      const renderCard = (
        hotel: iHolidayQuoteItineraryBlock,
        index: number,
        dragHandleProps?: DraggableProvidedDragHandleProps | null
      ) => {
        const card = (
          <div
            key={hotel.id}
            id={`itinerary-${hotel.id}`}
            className="scroll-mt-28"
          >
            <HotelStayCard
              hotel={hotel}
              onView={() => setViewHotelId(hotel.id)}
            />
          </div>
        );
        return renderHotel
          ? renderHotel(hotel, index, card, dragHandleProps)
          : card;
      };

      if (droppableId) {
        return (
          <StrictModeDroppable droppableId={droppableId}>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="grid grid-cols-1 gap-5 md:grid-cols-2"
              >
                {hotels.map((hotel, index) => (
                  <Draggable
                    key={hotel.id}
                    draggableId={hotel.id}
                    index={index}
                  >
                    {(dragProvided) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                      >
                        {renderCard(
                          hotel,
                          index,
                          dragProvided.dragHandleProps
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </StrictModeDroppable>
        );
      }

      return (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {hotels.map((hotel, index) => renderCard(hotel, index))}
        </div>
      );
    }

    if (layout === "accordion" || layout === "collapsible") {
      const renderStay = (hotel: iHolidayQuoteItineraryBlock, index: number) => {
        const open = Boolean(openIds[hotel.id]);
        const meta = metaFor(hotel);
        return (
          <ExpandableStay
            key={hotel.id}
            hotel={hotel}
            index={index}
            nights={meta?.nights}
            location={meta?.location}
            open={open}
            renderHotel={renderHotel}
            onToggle={() => {
              if (layout === "accordion") {
                // Allow fully collapsed — do not force another panel open.
                setOpenIds(open ? {} : { [hotel.id]: true });
              } else {
                setOpenIds((prev) => ({
                  ...prev,
                  [hotel.id]: !prev[hotel.id],
                }));
              }
            }}
          />
        );
      };

      if (droppableId) {
        return (
          <StrictModeDroppable droppableId={droppableId}>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="space-y-3"
              >
                {hotels.map((hotel, index) => (
                  <Draggable
                    key={hotel.id}
                    draggableId={hotel.id}
                    index={index}
                  >
                    {(dragProvided) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        {...dragProvided.dragHandleProps}
                      >
                        {renderStay(hotel, index)}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </StrictModeDroppable>
        );
      }

      return (
        <div className="space-y-3">
          {hotels.map((hotel, index) => renderStay(hotel, index))}
        </div>
      );
    }

    if (layout === "vertical_tabs") {
      return (
        <div className="grid gap-4 md:grid-cols-[minmax(0,240px)_minmax(0,1fr)]">
          <div className="flex gap-2 overflow-x-auto md:flex-col md:overflow-visible">
            {hotels.map((hotel, index) => {
              const meta = metaFor(hotel);
              return (
                <StayTabButton
                  key={hotel.id}
                  hotel={hotel}
                  index={index}
                  nights={meta?.nights}
                  location={meta?.location}
                  active={index === safeIndex}
                  orientation="vertical"
                  onClick={() => setActiveIndex(index)}
                />
              );
            })}
          </div>
          <HotelCard
            hotel={activeHotel}
            index={safeIndex}
            renderHotel={renderHotel}
          />
        </div>
      );
    }

    // horizontal_tabs (default)
    return (
      <div className="space-y-4">
        {hotels.length > 1 ? (
          <div className="flex gap-2 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]">
            {hotels.map((hotel, index) => {
              const meta = metaFor(hotel);
              return (
                <StayTabButton
                  key={hotel.id}
                  hotel={hotel}
                  index={index}
                  nights={meta?.nights}
                  location={meta?.location}
                  active={index === safeIndex}
                  onClick={() => setActiveIndex(index)}
                />
              );
            })}
          </div>
        ) : null}
        <HotelCard
          hotel={activeHotel}
          index={safeIndex}
          renderHotel={renderHotel}
        />
      </div>
    );
  };

  return (
    <>
      <section
        id="trip-section-stays"
        className="scroll-mt-28 bg-white p-4 sm:p-6 space-y-5"
        style={{
          borderRadius: quoteThemeCssVars.radiusCard,
          boxShadow: quoteThemeCssVars.shadowCard,
        }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2
            className="flex items-center gap-2 text-xl sm:text-2xl font-semibold"
            style={{
              color: quoteThemeCssVars.blockHeading,
              fontFamily: quoteThemeCssVars.headingFont,
            }}
          >
            <BedDouble
              className="h-6 w-6"
              style={{ color: quoteThemeCssVars.primary }}
            />
            Accommodations
          </h2>

          {onLayoutChange ? (
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <span className="hidden sm:inline">Layout</span>
              <select
                className="rounded-lg border bg-white px-2 py-1.5 text-sm"
                value={layout}
                disabled={layoutSaving}
                onChange={(e) =>
                  handleLayoutChange(
                    e.target.value as QuoteNarrativeLayoutVariant
                  )
                }
              >
                {QUOTE_ACCOMMODATIONS_LAYOUT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>

        {renderLayouts()}

        {hotels.length > 1 && layout !== "hotel_cards" ? (
          <button
            type="button"
            onClick={() => setViewAllOpen(true)}
            className="w-full flex items-center justify-center gap-1 rounded-xl px-4 py-3 text-sm font-semibold text-white"
            style={quoteThemeColorStyles.primaryButton}
          >
            View all
            <ChevronRight className="h-4 w-4" />
          </button>
        ) : null}
      </section>

      <QuoteOverlayModal
        open={viewAllOpen}
        onClose={() => setViewAllOpen(false)}
        title="All accommodations"
        className="max-w-4xl"
      >
        <div className="space-y-8">
          {hotels.map((hotel, index) => (
            <HotelCard
              key={hotel.id}
              hotel={hotel}
              index={index}
              renderHotel={renderHotel}
              withAnchor={false}
            />
          ))}
        </div>
      </QuoteOverlayModal>

      <QuoteOverlayModal
        open={Boolean(viewHotel)}
        onClose={() => setViewHotelId(null)}
        title={
          viewHotel?.accommodationData?.accommodationName ||
          "Accommodation"
        }
        className="max-w-4xl"
      >
        {viewHotel ? (
          <HotelCard
            hotel={viewHotel}
            index={Math.max(0, viewHotelIndex)}
            renderHotel={renderHotel}
            withAnchor={false}
          />
        ) : null}
      </QuoteOverlayModal>
    </>
  );
}
